// src/components/issues/tableau/MenuNouvelleVue.tsx — SCE Issues
// Le « + » au bout des vues : enregistre les filtres, le tri et le regroupement
// en cours sous un nom, qui devient une pastille de plus (vues personnelles,
// mémorisées avec les autres réglages du tableau).

import { useState } from 'react'
import { Popover } from '@/components/ui/Popover'
import {
  DEFINITIONS_COLONNES,
  LIBELLE_REGROUPEMENT,
  problemeNomVue,
  triEffectif,
  type ColonneId,
  type ReglagesTableau,
  type VuePredefinie,
} from '@/utils/tableauProblemes'
import { texteValeur } from './valeurs'

export interface MenuNouvelleVueProps {
  ancre: HTMLElement
  reglages: ReglagesTableau
  vuesFixes: readonly VuePredefinie[]
  onEnregistrer: (nom: string) => void
  onClose: () => void
}

/** Ce que la vue retiendra, en clair. */
function resume(r: ReglagesTableau): string[] {
  const lignes = (Object.entries(r.filtres) as [ColonneId, string[]][])
    .filter(([, v]) => v.length > 0)
    .map(([col, v]) => `${DEFINITIONS_COLONNES[col].libelle} : ${v.map((x) => texteValeur(col, x)).join(', ')}`)
  if (r.mesDeclarations) lignes.push('Mes déclarations')
  if (r.suivis) lignes.push('Suivis')
  if (lignes.length === 0) lignes.push('Aucun filtre')
  lignes.push(
    `Tri : ${triEffectif(r)
      .map((t) => `${DEFINITIONS_COLONNES[t.col].libelle} ${t.sens === 'asc' ? '↑' : '↓'}`)
      .join(' puis ')}`,
  )
  lignes.push(r.regroupement ? `Regroupé par ${LIBELLE_REGROUPEMENT[r.regroupement]}` : 'Sans regroupement')
  return lignes
}

export function MenuNouvelleVue({ ancre, reglages, vuesFixes, onEnregistrer, onClose }: MenuNouvelleVueProps): JSX.Element {
  const [nom, setNom] = useState('')
  const [tente, setTente] = useState(false)
  const probleme = problemeNomVue(nom, reglages, vuesFixes)

  function enregistrer(): void {
    setTente(true)
    if (probleme) return
    onEnregistrer(nom.trim())
    onClose()
  }

  return (
    <Popover ancre={ancre} onClose={onClose} titre="Enregistrer la vue" largeur={300}>
      <form
        className="vue-nouvelle"
        onSubmit={(e) => {
          e.preventDefault()
          enregistrer()
        }}
      >
        <label className="vue-nouvelle-libelle" htmlFor="nom-vue">
          Enregistrer la vue en cours
        </label>
        <input
          id="nom-vue"
          className="popover-recherche"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder="Nom, ex. NC-S ELEC"
          maxLength={60}
          autoFocus
          autoComplete="off"
        />
        {tente && probleme && <p className="vue-nouvelle-erreur">{probleme}</p>}
        <ul className="vue-nouvelle-resume">
          {resume(reglages).map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
        <div className="vue-nouvelle-actions">
          <button type="button" onClick={onClose}>
            Annuler
          </button>
          <button type="submit" className="vue-nouvelle-ok">
            Enregistrer
          </button>
        </div>
      </form>
    </Popover>
  )
}
